<template>
	<div ref="container" class="horizontal-panel">
		<div
			ref="left"
			class="horizontal-panel-left"
			:style="{
				...leftStyle,
				overflowX
			}"
		>
			<slot
				name="left"
				:leftWidth="leftWidth"
			/>
		</div>

		<div
			v-if="!disabled"
			class="resize-panel"
			:style="{ width: `${resizePanelWidth}px` }"

			@mousedown.stop.prevent="onStartResizeColumn($event)"
		/>

		<div
			ref="right"
			class="horizontal-panel-right"
			:style="{
				...rightStyle,
				overflowX
			}"
		>
			<slot
				name="right"
				:rightWidth="rightWidth"
			/>
		</div>

		<slot name="dialogs" />

		<div
			v-if="!realTimeResize"
			v-show="resizing"
			ref="preview"
			class="resize-preview"
			:style="{ width: `${previewWidth}px` }"
		/>

	</div>
</template>

<script>
export default {
	name: 'TResizableHorizontalPanel',
	props: {
		leftPercentage: {
			type: [Number, String],
			default: 50
		},

		minLeftPercentage: {
			type: [Number, String],
			default: 5
		},

		minLeftWidth: { type: Number },

		minRightPercentage: {
			type: [Number, String],
			default: 5
		},

		minRightWidth: { type: [Number, String] },

		localStorageId: { type: String },

		disabled: { type: Boolean },

		/**
		 * Resize panes as you move mouse, instead of doing actual dom resize after releasing mouse
		 */
		realTimeResize: {
			type: Boolean,
			default: false,
		},

		/**
		 * Setting to visible you to force the left pane having minimum the width of its children
		 */
		overflowX: {
			type: String,
			default: 'hidden'
		}
	},
	data() {

		return {
			resizing: false,
			leftFlex: 50,
			nextLeftFlexPercentage: 50,
			observer: null,
			containerWidth: 0,
			leftWidth: null,
			rightWidth: null,
			resizePanelWidth: 6,
			previewWidth: 3,
		};
	},
	computed: {
		showLeft() {
			return Boolean(this.$slots['left']);
		},
		showRight() {
			return Boolean(this.$slots['right']);
		},
		leftFlexValue() {
			return this.disabled ? 0 : this.leftFlex;
		},
		leftStyle() {
			return { flex: `${ this.leftFlexValue } 1 0%` };
		},
		rightStyle() {
			return { flex: `${ 100 - this.leftFlexValue } 1 0%` };
		},
		minLeft() {
			if (this.minLeftWidth && this.containerWidth > 0) {
				return (this.minLeftWidth / this.containerWidth) * 100;
			}
			return this.minLeftPercentage;
		},
		minRight() {
			if (this.minRightWidth && this.containerWidth > 0) {
				return (this.minRightWidth / this.containerWidth) * 100;
			}
			return this.minRightPercentage;
		}
	},
	watch: {
		localStorageId(newVal, oldVal) {

			if (newVal === oldVal) return;

			else if (newVal) this.updateWidthsFromLocalStorage();
			else this.calculateLeftFlex();
		}
	},
	async mounted() {

		await this.$nextTick();

		let { container } = this.$refs;

		if (container) {
			this.observer = new ResizeObserver(() => {

				let { width } = container.getBoundingClientRect();

				this.containerWidth = width;
			});
			this.observer.observe(container);

			let { width } = container.getBoundingClientRect();
			this.containerWidth = width;
		}

		this.calculateLeftFlex();
		this.updateWidthsFromLocalStorage();
	},
	beforeDestroy() {
		if (this.observer) this.observer.disconnect();
	},
	methods: {
		getConstrainedLeftFlex(minLeft, value, minRight) {

			return Math.min(

				Math.max(
					minLeft,
					value
				),

				100 - minRight
			);
		},
		calculateLeftFlex() {
			this.leftFlex = this.getConstrainedLeftFlex(this.minLeft, this.leftPercentage, this.minRight);

			[this.leftWidth, this.rightWidth]
				= this.calculateLeftAndRightWidths(this.containerWidth, this.resizePanelWidth, this.leftFlex);
		},

		calculateLeftAndRightWidths(containerWidth, resizePanelWidth, leftFlex) {

			let leftWidth =
				( containerWidth - resizePanelWidth )
				* ( leftFlex / 100 );

			let rightWidth =
				containerWidth
				- leftWidth
				- resizePanelWidth;

			return [leftWidth, rightWidth];
		},

		updateWidthsFromLocalStorage() {

			if (this.localStorageId) {

				let storedLeftFlexPercentage = localStorage.getItem(`${this.localStorageId}/horizontalResizePercentage`);

				if (!storedLeftFlexPercentage)
					return;

				this.nextLeftFlexPercentage = this.getConstrainedLeftFlex(this.minLeft, storedLeftFlexPercentage, this.minRight);

				if (storedLeftFlexPercentage !== this.nextLeftFlexPercentage)
					this.setLocalStorage(this.nextLeftFlexPercentage);

				this.setWidths();
			}
		},

		setWidths() {
			this.leftFlex = this.nextLeftFlexPercentage;

			[this.leftWidth, this.rightWidth]
				= this.calculateLeftAndRightWidths(this.containerWidth, this.resizePanelWidth, this.leftFlex);
		},

		addResizeListeners(mouseMoveListener, mouseUpListener) {

			window.addEventListener('mousemove', mouseMoveListener);

			window.addEventListener('mouseup', mouseUpListener);

		},
		removeResizeListeners(mouseMoveListener, mouseUpListener) {

			window.removeEventListener('mousemove', mouseMoveListener);

			window.removeEventListener('mouseup', mouseUpListener);

		},
		onStartResizeColumn(e) {

			this.resizing = true;

			let resizeMouseUpListener = (e) => {

				e.preventDefault();
				e.stopPropagation();


				this.setWidths();

				this.setLocalStorage(this.leftFlex);
				this.removeResizeListeners(resizeMouseMoveListener, resizeMouseUpListener);

				setTimeout(() => this.resizing = false, 100);
			};
			let resizeMouseMoveListener = (e) => {

				e.preventDefault();
				e.stopPropagation();

				let { left } = this.$refs.container.getBoundingClientRect();

				let relative = e.pageX - left;
				let relativePercentOfContainerWidth = ( relative / this.containerWidth ) * 100;

				this.nextLeftFlexPercentage = this.getConstrainedLeftFlex(
					this.minLeft,
					relativePercentOfContainerWidth,
					this.minRight
				);

				if ((e.buttons & 1) === 0) {  // Left mouse is not down


					this.setWidths();

					this.setLocalStorage(this.leftFlex);
					this.removeResizeListeners(resizeMouseMoveListener, resizeMouseUpListener);

					setTimeout(() => this.resizing = false, 100);

					return;
				}

				if (this.realTimeResize) {

					this.setWidths();

				} else {

					this.$refs.preview.style.left =
						`${
							(this.nextLeftFlexPercentage / 100)
							* this.containerWidth
							- 102
						}px`;
				}
			};
			resizeMouseMoveListener(e); // Run it once

			this.addResizeListeners(resizeMouseMoveListener, resizeMouseUpListener);
		},

		setLocalStorage(leftFlex) {

			if (this.localStorageId) {

				localStorage.setItem(`${this.localStorageId}/horizontalResizePercentage`, leftFlex);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.horizontal-panel {
	display: flex;
	position: relative;
}
.resize-preview {
	position: absolute;
	border-left: 100px solid transparent;
	border-right: 100px solid transparent;
	background-clip: content-box;
	background-color: #c1c1c1;
	top: 0;
	bottom: 0;
	z-index: 9999;
	cursor: ew-resize;
}

.horizontal-panel, .horizontal-panel-left, .horizontal-panel-right, .horizontal-panel-right-inner{
	height: 100%;
}

.horizontal-panel-left {
	box-shadow: 4px 0 2px -2px rgba(0, 0, 0, 0.1);
	z-index: 4;
}

.horizontal-panel-right {
	position: relative;
	width: auto;
	z-index: 3;
	&.hidden {
		width: 0px;
	}
}

.horizontal-panel-right-inner {
	transform: translate(0px);
	box-shadow: -4px 0 2px -2px rgba(0, 0, 0, 0.1);
}

.horizontal-panel-content {
	flex:1;
	height: 100%;
	width: 100%;
	position: relative;
}

.resize-panel {
	background-color: #7c8b98;
	user-select: none;
	width: 6px;
	background-clip: content-box;
	height: 100%;
	box-sizing: content-box;
	cursor: ew-resize;
	flex-shrink: 0;
}
</style>
